import { useMutation, useQuery } from "react-query";
import {
  completeCompanyDocumentUploads,
  createCompanyDocumentFolder,
  deleteCompanyDocumentItems,
  getCompanyDocumentBrowser,
  getCompanyDocumentDownloadUrl,
  getRelatedCompanyDocuments,
  moveCompanyDocumentFiles,
  presignCompanyDocumentUploads,
  queueCompanyDocumentDownload,
  searchCompanyDocuments,
} from "../api/companyDocuments";
import {
  CompanyDocumentSearchRequest,
  CompanyDocumentDownloadUrlRequest,
  CompleteCompanyDocumentUploadsRequest,
  CreateCompanyDocumentFolderRequest,
  DeleteCompanyDocumentItemsRequest,
  PresignCompanyDocumentUploadsRequest,
  QueueCompanyDocumentDownloadRequest,
  RelatedDocumentFilter,
  MoveCompanyDocumentFilesRequest,
} from "../types/companyDocuments";
import { queryClient } from "./client";

export const companyDocumentQueryKeys = {
  contents: (companyId: string, folderId: string | null) =>
    ["companyDocuments", "contents", companyId, folderId] as const,
  search: (companyId: string, request: CompanyDocumentSearchRequest) =>
    ["companyDocuments", "search", companyId, request] as const,
  related: (companyId: string, source: RelatedDocumentFilter) =>
    ["companyDocuments", "related", companyId, source] as const,
};

export function useCompanyDocumentBrowser(
  companyId: string,
  folderId: string | null
) {
  return useQuery({
    queryKey: companyDocumentQueryKeys.contents(companyId, folderId),
    queryFn: () => getCompanyDocumentBrowser(folderId),
    enabled: Boolean(companyId),
    keepPreviousData: true,
  });
}

export function useCompanyDocumentSearch(
  companyId: string,
  request: CompanyDocumentSearchRequest,
  enabled = true
) {
  return useQuery({
    queryKey: companyDocumentQueryKeys.search(companyId, request),
    queryFn: () => searchCompanyDocuments(request),
    enabled: Boolean(companyId) && enabled,
    keepPreviousData: true,
  });
}

export function useRelatedCompanyDocuments(
  companyId: string,
  source: RelatedDocumentFilter
) {
  return useQuery({
    queryKey: companyDocumentQueryKeys.related(companyId, source),
    queryFn: () => getRelatedCompanyDocuments(source),
    enabled: Boolean(companyId),
    keepPreviousData: true,
  });
}

export function useCreateCompanyDocumentFolder(companyId: string) {
  return useMutation({
    mutationKey: ["companyDocuments", "createFolder", companyId],
    mutationFn: (request: CreateCompanyDocumentFolderRequest) =>
      createCompanyDocumentFolder(request),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "companyDocuments",
        "contents",
        companyId,
      ]);
      queryClient.invalidateQueries(["companyDocuments", "search", companyId]);
    },
  });
}

export function usePresignCompanyDocumentUploads() {
  return useMutation({
    mutationKey: ["companyDocuments", "presignUploads"],
    mutationFn: (request: PresignCompanyDocumentUploadsRequest) =>
      presignCompanyDocumentUploads(request),
  });
}

export function useCompleteCompanyDocumentUploads(companyId: string) {
  return useMutation({
    mutationKey: ["companyDocuments", "completeUploads", companyId],
    mutationFn: (request: CompleteCompanyDocumentUploadsRequest) =>
      completeCompanyDocumentUploads(request),
    onSuccess: () => {
      // The application-wide query stale time is six hours. A successful
      // confirmation must therefore invalidate the active folder explicitly.
      queryClient.invalidateQueries([
        "companyDocuments",
        "contents",
        companyId,
      ]);
      queryClient.invalidateQueries(["companyDocuments", "search", companyId]);
    },
  });
}

export function useCompanyDocumentDownloadUrl() {
  return useMutation({
    mutationKey: ["companyDocuments", "downloadUrl"],
    mutationFn: (request: CompanyDocumentDownloadUrlRequest) =>
      getCompanyDocumentDownloadUrl(request),
  });
}

export function useQueueCompanyDocumentDownload() {
  return useMutation({
    mutationKey: ["companyDocuments", "queueDownload"],
    mutationFn: (request: QueueCompanyDocumentDownloadRequest) =>
      queueCompanyDocumentDownload(request),
  });
}

export function useDeleteCompanyDocumentItems(companyId: string) {
  return useMutation({
    mutationKey: ["companyDocuments", "deleteItems", companyId],
    mutationFn: (request: DeleteCompanyDocumentItemsRequest) =>
      deleteCompanyDocumentItems(request),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "companyDocuments",
        "contents",
        companyId,
      ]);
      queryClient.invalidateQueries(["companyDocuments", "search", companyId]);
    },
  });
}

export function useMoveCompanyDocumentFiles(companyId: string) {
  return useMutation({
    mutationKey: ["companyDocuments", "moveFiles", companyId],
    mutationFn: (request: MoveCompanyDocumentFilesRequest) =>
      moveCompanyDocumentFiles(request),
    onSuccess: () => {
      queryClient.invalidateQueries(["companyDocuments", "contents", companyId]);
      queryClient.invalidateQueries(["companyDocuments", "search", companyId]);
    },
  });
}
